import {booleanAttribute, Component, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {KENDO_INPUTS} from "@progress/kendo-angular-inputs";
import {provideTranslocoScope, TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {OAuthService} from 'angular-oauth2-oidc';
import {ContextMenuComponent, ContextMenuSelectEvent, KENDO_MENUS} from "@progress/kendo-angular-menu";
import {NgClass} from '@angular/common';

import {SimpleImageComponent} from '../simple-image/simple-image.component';
import {FonteIconeComponent} from '../fonte-icone/fonte-icone.component';
import {SimpleLibelleComponent} from '../simple-libelle/simple-libelle.component';
import {BarreNavigationComponent} from '../barre-navigation/barre-navigation.component';
import {LibelleDicoPipe} from '../Utilitaires/libelle-dico.pipe';
import {DonneesFicheEdition, donneesGestionFichesEdition} from '../../../../../val-fiches-edition/val-fiches-edition-lib/src/public-api';
import {ServicesCommuns} from '../../../../../val-definitions-communes-lib/src/public-api';
import {ValLangueService, ValNavigationService} from '../../../../../val-definitions-communes-lib/src/public-api';

//Composant bandeau application
@Component (
{
  selector: 'val-bandeau-application',
  imports: 
  [
    NgClass,
    KENDO_INPUTS,
    KENDO_MENUS,
    FonteIconeComponent,
    SimpleImageComponent,
    SimpleLibelleComponent,
    TranslocoPipe,
    ContextMenuComponent,
    LibelleDicoPipe
  ],
  providers:
  [
    provideTranslocoScope('valcomposantslib')
  ],
  templateUrl: './bandeau-application.component.html',
  styleUrl: './bandeau-application.component.scss'
})
export class BandeauApplicationComponent implements OnInit
{
  private valLangueService = inject (ValLangueService);
  private translocoService = inject (TranslocoService);
  //Les écrans du menu utilisateur s’affichent comme ceux de la barre de navigation, selon le mode de l’application
  private navigationService = inject (ValNavigationService);

  //Création
  //  Paramètres
  //    oAuthService : Service authentification
  constructor (private oAuthService: OAuthService) 
  {
    this.srcLogo = "";
    this.srcLogoEditeur = "";
    this.titre = "";
    this.dicoTitre = null;
    this.nomUtilisateur = "";
    this.rechercheOuverte = false;
    this.ouvert = true;
    this.barreNavigation = null;
    this.utiliserFichesOuvertes = false;
    this.choixLangue = false;
    this.lienGuideUtilisateur = "";
    this.navigationMesAbonnements = null;
    this.navigationMesPreferences = null;
    this.ouvrirFermer = new EventEmitter<boolean>;
    this.items = [];

    //Le menu se reconstruit à chaque changement de langue, pour déplacer la coche
    this.translocoService.langChanges$
      .pipe (takeUntilDestroyed ())
      .subscribe (() => this.ConstruireItems ());
  }

  //Source du logo de l’application
  @Input ()
  public srcLogo: string;

  //Source du logo de l’éditeur
  @Input ()
  public srcLogoEditeur: string;

  //Titre
  @Input ()
  public titre: string;

  //Dico du titre : le titre est traduit comme l’entrée de navigation dont il reprend le libellé
  @Input ()
  public dicoTitre: string | null | undefined;

  //Nom de l’utilisateur
  @Input ()
  public nomUtilisateur: string;

  //Indique si la recherche est ouverte
  @Input ({transform: booleanAttribute})
  public rechercheOuverte: boolean;

  //Indique si ouvert
  @Input ({transform: booleanAttribute})
  public ouvert: boolean;

  //Barre de navigation
  @Input ()
  public barreNavigation: BarreNavigationComponent | null;

  @Input ({transform: booleanAttribute})
  public utiliserFichesOuvertes: boolean;

  //Indique si le menu utilisateur propose le choix de la langue : à poser seulement par une application traduite
  @Input ({transform: booleanAttribute})
  public choixLangue: boolean;

  //Lien vers le guide utilisateur : le menu utilisateur ne propose l’entrée que s’il est renseigné
  @Input ()
  public lienGuideUtilisateur: string;

  //Commande de navigation vers l’écran « Mes abonnements » aux notifications (ex : ["/main", {outlets: {page: ["abonnements"]}}]) :
  //le menu utilisateur ne propose l’entrée que si elle est renseignée
  @Input ()
  public navigationMesAbonnements: any[] | null;

  //Commande de navigation vers l’écran « Mes préférences » (ex : ["/main", {outlets: {page: ["preferences"]}}]) :
  //le menu utilisateur ne propose l’entrée que si elle est renseignée
  @Input ()
  public navigationMesPreferences: any[] | null;

  //Evénement ouvrir / fermer
  @Output ()
  public ouvrirFermer: EventEmitter<boolean>;

  //Items du menu utilisateur
  public items: any[];

  //Initialisation : les entrées sont connues, le menu se reconstruit selon choixLangue
  public ngOnInit () : void
  {
    this.ConstruireItems ();
  }

  //Ouvre / ferme la recherche
  public OuvrirFermerRecherche () : void 
  {
    this.rechercheOuverte = !this.rechercheOuverte;

    if (!this.rechercheOuverte)
    {
      this.ouvert = false;

      this.OuvrirFermerBarreNavigation ();
      
      this.ouvrirFermer.emit (this.ouvert);
    }
  }

  //Ouvre / ferme
  public OuvrirFermer () : void 
  {
    this.ouvert = !this.ouvert;
    this.rechercheOuverte = this.ouvert;

    this.OuvrirFermerBarreNavigation ();

    this.ouvrirFermer.emit (this.ouvert);
  }

  //Ouvre / ferme la barre de navigation associée
  private OuvrirFermerBarreNavigation () : void 
  {
    if (this.barreNavigation && this.barreNavigation.ouvert !== this.ouvert)
      this.barreNavigation.OuvrirFermer ();
  }

  //Click sur un élément du menu
  //  Paramètres
  //    evenement : Données de l’événement
  public MenuClick (evenement: ContextMenuSelectEvent) : void
  {
    if (evenement.item.id === 1)
      this.Deconnecter ();
    else if (evenement.item.id === 3)
      void this.valLangueService.Changer (evenement.item.langue);
    else if (evenement.item.id === 4)
      window.open (this.lienGuideUtilisateur, "_blank", "noopener");
    else if (evenement.item.id === 5 && this.navigationMesAbonnements)
      void this.navigationService.Naviguer (this.navigationMesAbonnements);
    else if (evenement.item.id === 6 && this.navigationMesPreferences)
      this.AfficherMesPreferences ();
  }

  //Affiche l’écran « Mes préférences » dans la page principale : une fiche d’édition ouverte le masquerait
  private AfficherMesPreferences () : void
  {
    if (this.utiliserFichesOuvertes)
      this.AfficherPagePrincipale ();

    void this.navigationService.Naviguer (this.navigationMesPreferences!);
  }

  //Construit les items du menu utilisateur : choix de la langue, mes préférences, mes abonnements et guide utilisateur,
  //au-dessus de Déconnecter
  private ConstruireItems () : void
  {
    let items: any[];
    let langues: string[];
    let langueActive: string;

    items = [];
    langues = this.valLangueService.Langues;
    langueActive = this.valLangueService.LangueActive;

    if (this.choixLangue && langues.length > 1)
      items.push (
      {
        id: 2,
        cle: 'valcomposantslib.bandeau-application.menu-utilisateur.langue',
        icone: 'fas fa-globe',
        items: langues.map (langue => (
        {
          id: 3,
          langue: langue,
          texte: this.valLangueService.Libelle (langue),
          icone: langue === langueActive ? 'fas fa-check' : 'fas fa-fw'
        }))
      });

    if (this.navigationMesPreferences)
      items.push ({id: 6, cle: 'valcomposantslib.bandeau-application.menu-utilisateur.preferences', icone: 'fas fa-sliders-h'});

    if (this.navigationMesAbonnements)
      items.push ({id: 5, cle: 'valcomposantslib.bandeau-application.menu-utilisateur.mes-abonnements', icone: 'fas fa-bell'});

    if (this.lienGuideUtilisateur)
      items.push ({id: 4, cle: 'valcomposantslib.bandeau-application.menu-utilisateur.guide-utilisateur', icone: 'fas fa-question-circle'});

    items.push ({id: 1, cle: 'valcomposantslib.bandeau-application.menu-utilisateur.deconnecter', icone: 'fas fa-sign-out'});

    this.items = items;
  }

  //Déconnecte l’utilisateur
  public Deconnecter () : void 
  {
    sessionStorage.removeItem ("code_client");
    this.oAuthService.logOut ();
  }

  //Données des fiches d’édition
  public get DonneesFichesEdition () : DonneesFicheEdition[] 
  {
    return donneesGestionFichesEdition.donneesFichesEdition;
  }

  //Ouvre une fiche d’édition
  //  Paramètres
  //    donneesFicheEdition : Données de la fiche d’édition
  public OuvrirFiche (donneesFicheEdition: DonneesFicheEdition) : void 
  {
    ServicesCommuns.GestionFichesEdition.ReouvrirFiche (donneesFicheEdition);
  }

  //Ferme une fiche d’édition
  //  Paramètres
  //    donneesFicheEdition : Données de la fiche d’édition
  public FermerFiche (donneesFicheEdition: DonneesFicheEdition) : void 
  {
    ServicesCommuns.GestionFichesEdition.FermerFicheMenu (donneesFicheEdition);
  }

  //Affiche la page principale
  public AfficherPagePrincipale () : void 
  {
    ServicesCommuns.GestionFichesEdition.AfficherPagePrincipale ();
  }
}
